import type { Contact, CreateContactRequest, UpdateContactRequest } from '../types';

/** Scalar fields allowed on a minimal contact PUT (never sends customDates / eventTypeIds). */
export type PartialContactScalarPatch = Partial<
  Pick<
    UpdateContactRequest,
    | 'email'
    | 'phone'
    | 'notes'
    | 'birthday'
    | 'birthdayYearKnown'
    | 'addressLine1'
    | 'addressLine2'
    | 'city'
    | 'state'
    | 'postalCode'
    | 'country'
    | 'relationship'
    | 'preferredTone'
    | 'preferredMediaType'
    | 'preferredVisualStyle'
    | 'aiInstructions'
  >
>;

/**
 * Builds a PATCH-style PUT body for single-field (or few-field) updates from stale UI snapshots.
 * Omits customDates and eventTypeIds so a loaded contact cannot replace collections server-side.
 */
export function buildPartialContactUpdate(
  contact: Pick<Contact, 'firstName' | 'lastName'>,
  patch: PartialContactScalarPatch,
): UpdateContactRequest {
  return {
    firstName: contact.firstName.trim(),
    lastName: contact.lastName.trim(),
    ...patch,
  };
}

/**
 * Optional string fields on contact update: API treats null as omit and "" as clear (Bug #1437).
 * The website must send explicit empty strings when the user clears a field.
 */
export function clearableContactString(value: string | null | undefined): string {
  return (value ?? '').trim() === '' ? '' : (value ?? '').trim();
}

/**
 * Maps contact form output to a PUT body with explicit "" clears (never undefined for clearable scalars).
 */
export function buildContactUpdatePayload(
  values: CreateContactRequest,
): UpdateContactRequest {
  const birthday =
    values.birthday === undefined
      ? undefined
      : values.birthday === '' || values.birthday == null
        ? ''
        : values.birthday;

  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    email: clearableContactString(values.email),
    phone: clearableContactString(values.phone),
    notes: clearableContactString(values.notes),
    birthday,
    birthdayYearKnown: values.birthdayYearKnown,
    customDates: values.customDates,
    eventTypeIds: values.eventTypeIds,
    addressLine1: clearableContactString(values.addressLine1),
    addressLine2: clearableContactString(values.addressLine2),
    city: clearableContactString(values.city),
    state: clearableContactString(values.state),
    postalCode: clearableContactString(values.postalCode),
    country: clearableContactString(values.country),
    relationship: clearableContactString(values.relationship),
    preferredTone: clearableContactString(values.preferredTone),
    preferredMediaType: clearableContactString(values.preferredMediaType),
    preferredVisualStyle: clearableContactString(values.preferredVisualStyle),
    aiInstructions: clearableContactString(values.aiInstructions),
  };
}

/**
 * On edit, undefined means omit <c>Birthday</c> from the PUT body (Bug #1437 partial update).
 * Explicit clear is handled in ContactForm when the user empties birthday fields (sends "").
 */
export function resolveBirthdayForContactWrite(
  isUpdate: boolean,
  birthday: string | undefined,
  _yearKnown: boolean | undefined,
  _hasPartialMonthDay: boolean,
): string | undefined {
  if (!isUpdate) {
    return birthday;
  }

  if (birthday) {
    return birthday;
  }

  return undefined;
}
